import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRight, Phone, MapPin, ShieldCheck, Star, Sparkles, Bed, Bath, Home as HomeIcon, Calculator,
  ChevronDown, TrendingUp, CheckCircle2, X,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";
import { statusBadge, fmtListingPrice } from "@/lib/propertyStatus";
import { buildLocalBusinessJsonLd, buildFaqJsonLd } from "@/lib/seo";
import { fmtPrice, lbl } from "@/lib/terms";

/**
 * AgencySite — a full, ready-to-launch website that any Foundation
 * tenant gets automatically at /t/:slug.
 *
 * Every section reads from the tenant's brand config, so editing
 * `/admin/brand` updates the live site with zero code changes. Featured
 * listings pull straight from `/api/crm/public/properties?tenant=<slug>`
 * so the site stays fresh as the agency adds instructions.
 *
 * This is Foundation Sites Phase 1 — one beautiful, opinionated,
 * conversion-focused template. Future phases will add multiple templates
 * and per-tenant custom sections.
 */
export default function AgencySite() {
  const brand = useBrand();
  const market = useMarket();
  const { slug } = useParams();
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(slug);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const heroImg = brand.assets?.hero_image_url;
  const phoneHref = biz.phone ? `tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}` : null;
  const faqItems = brand.faq || [];

  useEffect(() => {
    // Hold until the tenant is known: on a custom domain the slug arrives
    // with the brand, and fetching before it does asks for the wrong
    // agency's properties. See useTenantSlug.
    if (!tenantKnown) return;
    api.get(`/crm/public/properties?tenant=${tenantSlug}&limit=6`)
      .then(({ data }) => setFeatured((data.items || []).slice(0, 6)))
      .catch(() => setFeatured([]));
    api.get(`/crm/public/stats?tenant=${tenantSlug}`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, [tenantSlug, tenantKnown]);

  const registerHref = tenantPath("register");
  const valuationHref = tenantPath("valuation");
  const listingsHref = tenantPath("properties");

  const agentsWord = market === "us" ? "Real Estate" : "Estate Agents";
  const seoTitle = biz.name ? `${biz.name} | ${agentsWord} in ${brand.regions_covered_prose || "Your Area"}` : undefined;
  const seoDescription = biz.elevator_pitch
    || (market === "us"
      ? `${biz.name || "We"} are an independent brokerage serving ${brand.regions_covered_prose || "your area"}. Free home valuation, live listings and honest, local advice.`
      : `${biz.name || "We"} are independent estate agents covering ${brand.regions_covered_prose || "your area"}. Free 60-second online valuation, live listings and honest, local advice.`);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        image={heroImg ? mediaUrl(heroImg) : undefined}
        jsonLd={[
          buildLocalBusinessJsonLd(brand, { imageUrl: heroImg ? mediaUrl(heroImg) : undefined }),
          buildFaqJsonLd(faqItems),
        ].filter(Boolean)}
      />
      <DemoReferralBanner />
      <SiteHeader />

      <main className="flex-1">
        {/* ---- HERO ---- Cinematic asymmetric split. Boutique Cormorant
             headline paired with quiet Manrope subhead. Hero image floats
             on a deep-ink plate to feel gallery-like, not real-estate-y. */}
        <section className="relative overflow-hidden">
          {/* Ambient background wash — very subtle, keeps the ivory bg from feeling flat */}
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              background: "radial-gradient(60% 40% at 80% 0%, hsl(var(--accent) / 0.10), transparent 60%)",
            }}
          />
          <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 sm:py-28 relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7">
                <div className="overline">
                  {biz.tagline || lbl("independent_agent", market)}
                </div>
                <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-primary mt-6 leading-[1.02] tracking-tight">
                  {biz.hero_headline || (
                    <>
                      Selling homes in{" "}
                      <em className="italic" style={{ color: "hsl(var(--accent))" }}>
                        {brand.regions_covered_prose || "your area"}
                      </em>
                      , personally.
                    </>
                  )}
                </h1>
                <p className="text-lg text-muted-foreground mt-8 leading-relaxed max-w-xl">
                  {biz.hero_sub || (market === "us"
                    ? "Personal service, market-leading marketing and honest advice from the team that lives in your neighborhood."
                    : "Personal service, market-leading marketing and honest advice from the team that lives in your neighbourhood.")}
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Link to={valuationHref} data-testid="site-cta-valuation">
                    <Button size="lg" className="h-12 px-7 rounded-sm sharp-btn">
                      {lbl("free_valuation", market)} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={registerHref} data-testid="site-cta-register">
                    <Button size="lg" variant="outline" className="h-12 px-7 rounded-sm border-primary/25 sharp-btn">
                      <Sparkles className="w-4 h-4 mr-2" />{lbl("register_buyer", market)}
                    </Button>
                  </Link>
                  {phoneHref && (
                    <a href={phoneHref} data-testid="site-cta-call">
                      <Button size="lg" variant="ghost" className="h-12 px-7 rounded-sm">
                        <Phone className="w-4 h-4 mr-2" />{biz.phone}
                      </Button>
                    </a>
                  )}
                </div>
                {brand.trust_badges?.length > 0 && (
                  <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-foreground">
                    {brand.trust_badges.slice(0, 3).map((b, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-accent" /> {b.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hero image plate — offset ink block behind + subtle grain on top */}
              <div className="lg:col-span-5 hidden lg:block relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-3 rounded-sm"
                  style={{ background: "hsl(var(--primary) / 0.06)", transform: "translate(14px, 14px)" }}
                />
                <div className="relative aspect-[4/5] overflow-hidden rounded-sm grain-overlay">
                  {heroImg ? (
                    <img
                      src={heroImg}
                      alt={brand.assets?.hero_caption || biz.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src="https://images.unsplash.com/photo-1582913130063-8318329a94a3?auto=format&fit=crop&w=1200&q=70"
                      alt={biz.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                {brand.assets?.hero_caption && (
                  <div className="mt-4 text-xs uppercase tracking-[0.28em] text-muted-foreground text-right">
                    {brand.assets.hero_caption}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ---- STATS STRIP ---- */}
        {/* On by default for the template — a tenant can opt out via
            brand.show_stats_strip: false without touching this component. */}
        {brand.show_stats_strip !== false && stats && (stats.on_market > 0 || stats.sold_or_let > 0) && (
          <section className="border-t border-b border-border bg-primary text-primary-foreground">
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-8 flex flex-wrap items-center justify-center gap-x-14 gap-y-4 text-center">
              {stats.on_market > 0 && (
                <div>
                  <div className="font-serif-display text-3xl">{stats.on_market}</div>
                  <div className="text-xs uppercase tracking-[0.2em] opacity-70 mt-1 flex items-center gap-1.5 justify-center">
                    <TrendingUp className="w-3.5 h-3.5" /> {lbl("stat_on_market", market)}
                  </div>
                </div>
              )}
              {stats.sold_or_let > 0 && (
                <div>
                  <div className="font-serif-display text-3xl">{stats.sold_or_let}</div>
                  <div className="text-xs uppercase tracking-[0.2em] opacity-70 mt-1 flex items-center gap-1.5 justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {lbl("stat_sold_or_let", market)}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ---- FEATURED LISTINGS ---- */}
        <section className="border-t border-border py-20 lg:py-24 bg-secondary/15">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="overline">On the market</div>
                <h2 className="font-serif-display text-4xl lg:text-5xl text-primary mt-3">Featured properties</h2>
              </div>
              <Link to={listingsHref} className="link-hover text-sm text-primary inline-flex items-center gap-1" data-testid="site-see-all-properties">
                See all properties <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {featured.length === 0 ? (
              <div className="mt-12 text-center text-muted-foreground border border-dashed border-border rounded-sm py-16">
                No listings on the market right now.{" "}
                <Link to={registerHref} className="text-primary underline">Register for property alerts</Link>
                {" "}and we'll email you the moment we have something.
              </div>
            ) : (
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featured.map((p, i) => {
                  const price = fmtListingPrice(p, market);
                  const cover = (p.images || []).find((i) => i.id === p.primary_image_id) || (p.images || [])[0];
                  const badge = statusBadge(p.status, p.listing_type, market);
                  return (
                    <Link
                      key={p.id}
                      to={tenantPath(`properties/${p.id}`)}
                      data-testid={`site-featured-${p.id}`}
                      className="group stagger-item elevate border border-border bg-card rounded-sm overflow-hidden hover:border-primary"
                    >
                      {cover ? (
                        <div className="aspect-video overflow-hidden relative">
                          {badge && (
                            <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[11px] uppercase tracking-widest">
                              {badge.label}
                            </span>
                          )}
                          <img
                            src={mediaUrl(cover.thumb_url || cover.url)}
                            alt={p.address_line1}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-secondary/40 flex items-center justify-center border-b border-border">
                          <HomeIcon className="w-8 h-8 text-muted-foreground/40" />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="font-serif-display text-xl text-primary leading-tight">{p.address_line1}</div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {p.town || ""} · {p.postcode}
                        </div>
                        <div className="font-serif-display text-2xl text-primary mt-4">{price}</div>
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {p.bedrooms}</span>
                          <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {p.bathrooms}</span>
                          {p.size_sqft > 0 && <span>{p.size_sqft.toLocaleString()} sqft</span>}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ---- SERVICES ---- */}
        <section className="py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">What we do</div>
              <h2 className="font-serif-display text-4xl text-primary mt-3">Everything a modern move demands</h2>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <ServiceCard icon={HomeIcon} title={lbl("svc_sales_title", market)} to={tenantPath("sell")}
                body={lbl("svc_sales_body", market)}
              />
              <ServiceCard icon={MapPin} title={lbl("svc_lettings_title", market)} to={tenantPath("let")}
                body={lbl("svc_lettings_body", market)}
              />
              <ServiceCard icon={Sparkles} title={lbl("svc_newhomes_title", market)}
                body={lbl("svc_newhomes_body", market)}
              />
              <ServiceCard icon={Calculator} title={lbl("svc_tools_title", market)}
                body={lbl("svc_tools_body", market)}
                href={market === "us" ? undefined : "/calculators"}
              />
            </div>
          </div>
        </section>

        {/* ---- TESTIMONIALS ---- */}
        {brand.testimonials?.length > 0 ? (
          <section className="border-t border-border py-20 bg-primary text-primary-foreground">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
              <div className="text-center">
                <Star className="w-6 h-6 text-accent mx-auto" />
                <h2 className="font-serif-display text-3xl sm:text-4xl mt-6">What our clients say</h2>
              </div>
              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
                {brand.testimonials.slice(0, 3).map((t, i) => (
                  <div key={i} className="stagger-item">
                    <div className="flex gap-0.5 text-accent">
                      {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-current" />)}
                    </div>
                    <blockquote className="font-serif-display text-lg mt-4 leading-relaxed opacity-95">
                      “{t.quote}”
                    </blockquote>
                    <div className="text-xs mt-5 opacity-70 tracking-widest uppercase">
                      {t.author}{t.location ? `, ${t.location}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : brand.testimonial?.quote && (
          <section className="border-t border-border py-20 bg-primary text-primary-foreground">
            <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
              <Star className="w-6 h-6 text-accent mx-auto" />
              <blockquote className="font-serif-display text-3xl sm:text-4xl mt-8 leading-[1.35]">
                “{brand.testimonial.quote}”
              </blockquote>
              <div className="text-sm mt-8 opacity-80 tracking-widest uppercase">{brand.testimonial.attribution}</div>
            </div>
          </section>
        )}

        {/* ---- AREAS ---- */}
        {brand.areas_served?.length > 0 && (
          <section className="py-20">
            <div className="mx-auto max-w-7xl px-6 lg:px-10 text-center">
              <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Where we work</div>
              <h2 className="font-serif-display text-4xl text-primary mt-3">Local specialists, wider reach</h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
                {brand.regions_covered_prose ? `Covering ${brand.regions_covered_prose}.` : ""}
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {brand.areas_served.map((a) => (
                  <Link
                    key={a}
                    to={tenantPath(`area/${a.toLowerCase().replace(/\s+/g, "-")}`)}
                    className="px-4 py-2 text-sm border border-border rounded-full text-primary bg-card hover:border-primary hover:bg-secondary/20 transition-colors"
                  >
                    {a}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ---- FAQ ---- */}
        {faqItems.length > 0 && (
          <section className="border-t border-border py-20 lg:py-24">
            <div className="mx-auto max-w-3xl px-6 lg:px-10">
              <div className="text-center">
                <div className="overline">Questions</div>
                <h2 className="font-serif-display text-4xl text-primary mt-3">Frequently asked</h2>
              </div>
              <div className="mt-12 divide-y divide-border border-t border-b border-border">
                {faqItems.map((f, i) => {
                  const open = openFaq === i;
                  const panelId = `faq-panel-${i}`;
                  return (
                    <div key={i}>
                      <button
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        className="w-full flex items-center justify-between gap-4 py-5 text-left"
                        data-testid={`faq-toggle-${i}`}
                        aria-expanded={open}
                        aria-controls={panelId}
                      >
                        <span className="font-serif-display text-lg text-primary">{f.question}</span>
                        <ChevronDown aria-hidden="true" className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <p id={panelId} role="region" className="pb-5 text-muted-foreground leading-relaxed pr-8">{f.answer}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ---- FINAL CTA ---- */}
        <section className="relative overflow-hidden border-t border-border py-24 bg-secondary/10">
          <div
            className="absolute inset-0 pointer-events-none opacity-50"
            style={{
              background: "radial-gradient(50% 60% at 50% 0%, hsl(var(--accent) / 0.08), transparent 65%)",
            }}
          />
          <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center relative">
            <div className="flourish text-2xl mb-2">The next chapter</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-primary">Thinking of moving?</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed">
              {market === "us"
                ? "Request a free home valuation from a local agent, or register as a buyer and we'll reach out the moment something matching hits the market."
                : "Get an instant, 60-second valuation of your home using HM Land Registry data, or register as a buyer and we'll come to you the moment something matching lands."}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to={valuationHref}>
                <Button size="lg" className="h-12 px-7 rounded-sm">{lbl("free_valuation", market)}<ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
              <Link to={registerHref}>
                <Button size="lg" variant="outline" className="h-12 px-7 rounded-sm border-primary/20">
                  {lbl("register_buyer", market)}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * DemoReferralBanner — only appears when a visitor arrives via the
 * "open in a new tab" link on Foundation's own homepage demo (which tags
 * the URL with ?ref=demo). Never shown to this tenant's own real,
 * organic visitors — this is a genuine live customer site, not a
 * purpose-built demo fixture, so it must stay silent by default.
 */
function DemoReferralBanner() {
  const [params] = useSearchParams();
  const [dismissed, setDismissed] = useState(false);
  if (params.get("ref") !== "demo" || dismissed) return null;
  return (
    <div className="relative bg-primary text-primary-foreground text-sm" data-testid="demo-referral-banner">
      <div className="mx-auto max-w-7xl px-10 lg:px-14 py-2.5 flex items-center justify-center gap-2.5 text-center flex-wrap">
        <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: "hsl(var(--accent))" }} />
        <span>You're viewing a real Foundation customer's website. Yours would carry your own branding, automatically.</span>
        <Link to="/" className="underline underline-offset-2 font-medium whitespace-nowrap hover:opacity-80 transition-opacity">
          See Foundation Sites →
        </Link>
      </div>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        data-testid="demo-referral-dismiss"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-foreground/60 hover:text-primary-foreground"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, body, href, to }) {
  const clickable = !!(to || href);
  const Wrapper = to ? Link : href ? "a" : "div";
  const props = to ? { to, className: "block group" } : href ? { href, target: "_self", className: "block group" } : { className: "block" };
  return (
    <Wrapper {...props}>
      <div
        className={`text-left h-full border border-border bg-card rounded-sm p-7 card-premium ${
          clickable ? "group-hover:border-accent/40 transition-colors" : ""
        }`}
      >
        <div className="w-11 h-11 rounded-sm bg-accent/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-accent" strokeWidth={1.75} />
        </div>
        <h3 className="font-serif-display text-2xl text-primary mt-6">{title}</h3>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{body}</p>
        {clickable && (
          <div className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary opacity-0 group-hover:opacity-100 transition-opacity">
            Learn more <ArrowRight className="w-3 h-3" />
          </div>
        )}
      </div>
    </Wrapper>
  );
}
